import { cn } from '@client/utils/styling/cn';
import { CircleAlert, CircleCheck, Info, type LucideIcon, TriangleAlert } from 'lucide-react';
import type { ReactNode, Ref } from 'react';

export type CalloutVariant = 'info' | 'success' | 'warning' | 'danger';

interface CalloutProps {
    /** The outer element, for callers that scroll or measure the callout. */
    ref?: Ref<HTMLDivElement>;
    /** Semantic role — drives the colour and the default icon. */
    variant?: CalloutVariant;
    /** Optional heading shown above the body in a stronger weight. */
    title?: ReactNode;
    /** Leading icon. Omit for the variant default; pass `null` to hide it. */
    icon?: ReactNode | null;
    children?: ReactNode;
    className?: string;
    /** A control at the trailing edge, such as a dismiss button; it sits beside the body, not under it. */
    action?: ReactNode;
    /**
     * Live-region role, for callouts that should be *announced* by screen readers — e.g. an error
     * summary that appears dynamically in response to an action. Omit for static callouts (no
     * announcement). Deliberately NOT derived from `variant`, so a static danger summary doesn't
     * force an assertive announcement of a long error list every time it renders.
     */
    role?: 'alert' | 'status';
}

const VARIANT_CLASS = {
    info: 'border-info-border bg-info-subtle text-info-foreground',
    success: 'border-success-border bg-success-subtle text-success-foreground',
    warning: 'border-warning-border bg-warning-subtle text-warning-foreground',
    danger: 'border-danger-border bg-danger-subtle text-danger-foreground',
} satisfies Record<CalloutVariant, string>;

const DEFAULT_ICON = {
    info: Info,
    success: CircleCheck,
    warning: TriangleAlert,
    danger: CircleAlert,
} satisfies Record<CalloutVariant, LucideIcon>;

/**
 * Flat, semantic callout for inline guidance, validation messages and error banners.
 *
 * Theme-aware (no `dark:` needed) — colour comes from the semantic status tokens. Use instead of
 * hand-rolled `rounded border bg-{hue}-50 …` boxes, which sprawl across hue families and need
 * manual `dark:` variants.
 *
 * @example
 * <Callout variant="danger" title="Import failed">Fix the highlighted rows and try again.</Callout>
 */
export function Callout({ ref, variant = 'info', title, icon, children, className, action, role }: CalloutProps) {
    const Icon = DEFAULT_ICON[variant];
    const resolvedIcon = icon === undefined ? <Icon className="mt-0.5 size-4 flex-shrink-0" /> : icon;

    return (
        <div
            ref={ref}
            role={role}
            className={cn('flex gap-2 rounded-md border p-3 text-sm', VARIANT_CLASS[variant], className)}
        >
            {resolvedIcon}
            <div className="min-w-0 flex-1">
                {title ? <div className="mb-0.5 font-semibold">{title}</div> : null}
                {children}
            </div>
            {action ? <div className="-my-1 -mr-1 flex-shrink-0 self-start">{action}</div> : null}
        </div>
    );
}
